import React, { useCallback } from 'react';
import { Stack, StackProps, styled, Typography, lighten } from '@mui/material';
import { Accept, useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';

interface DropZoneContainerProps extends StackProps {
  accepted: boolean;
  rejected: boolean;
}

const DropZoneContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'accepted' && prop !== 'rejected'}
)<DropZoneContainerProps>(({ theme, accepted, rejected }) => ({
  color: rejected ? theme.palette.error.main : accepted ? theme.palette.info.main : theme.palette.grey[600],
  borderColor: rejected ? theme.palette.error.main : accepted ? theme.palette.info.main : theme.palette.grey[300],
  backgroundColor:  rejected ? lighten(theme.palette.error.main, .9) : accepted ? lighten(theme.palette.info.main, .9) : undefined,
  cursor: 'pointer',
  alignItems: 'center',
  padding: theme.spacing(4),
  borderWidth: 2,
  borderRadius: 2,
  borderStyle: 'dashed',
  outline: 'none',
  transition: theme.transitions.create(['all'], {
    duration: theme.transitions.duration.standard,
  }),
  '&:hover' : {
    backgroundColor: rejected ? theme.palette.error.main : accepted ? lighten(theme.palette.info.main, .9) : theme.palette.grey[50],
  }
}));

type DropZoneProps = {
  disabled?: boolean,
  accept: Accept,
  onDrop: (files: File[]) => void
}

export const DropZone: React.FC<DropZoneProps> = (props: DropZoneProps) => {
  const { formatMessage } = useIntl();
  const { onDrop } = props;

  const handleOnDrop = useCallback((acceptedFiles: File[]) => {
    onDrop(acceptedFiles);
  }, [onDrop]);

  const {getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    accept: props.accept,
    onDrop: handleOnDrop
  });

  return(
    <DropZoneContainer {...getRootProps({
      accepted: isDragAccept,
      rejected: isDragReject,
    })}>
      <input {...getInputProps()} />
      <Typography>{formatMessage({ id: 'Dropzone.instruction', defaultMessage: 'Drag your files or click here to select them' })}</Typography>
    </DropZoneContainer>
  );
};